import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TPgAirwallexAccount,
	TPgClientAccessSettings,
	TPgFeeMatrix,
	TPgFxSettings,
	TPgLegalEntity,
	TPgListRequest,
	TPgListResponse,
	TPgPayabilityRule,
	TPgRecurringCycle,
	TPgRecurringRun,
	TPgScheduleSettings,
	TPgService,
	TPgServiceTypeMapping,
} from 'services/api/paymentGateway/types';
import {
	TPgCreateLegalEntityPayload,
	TPgCreatePayabilityRulePayload,
	TPgCreateServicePayload,
	TPgSaveKey,
	TPgSettingsStore,
	TPgUpdateAirwallexAccountPayload,
	TPgUpdateClientAccessPayload,
	TPgUpdateFeesPayload,
	TPgUpdateFxSettingsPayload,
	TPgUpdateLegalEntityPayload,
	TPgUpdatePayabilityRulePayload,
	TPgUpdateSchedulePayload,
	TPgUpdateServicePayload,
	TPgUpsertServiceTypeMappingPayload,
} from './types';

// ==========================================:
export const initialState: TPgSettingsStore = {
	services: null,
	servicesLoading: false,
	serviceTypeMappings: null,
	serviceTypeMappingsLoading: false,
	legalEntities: null,
	legalEntitiesLoading: false,
	airwallexAccounts: null,
	airwallexAccountsLoading: false,
	payabilityRules: null,
	payabilityRulesLoading: false,
	feeMatrix: null,
	feeMatrixLoading: false,
	fxSettings: null,
	fxSettingsLoading: false,
	schedule: null,
	scheduleLoading: false,
	clientAccess: null,
	clientAccessLoading: false,
	recurringRuns: null,
	recurringRunsLoading: false,
	recurringRunsLastParams: null,
	recurringCycles: null,
	recurringCyclesLoading: false,
	saveLoading: {},
	saveSuccess: {},
};

const startSave = (state: TPgSettingsStore, key: TPgSaveKey) => {
	state.saveLoading[key] = true;
	state.saveSuccess[key] = false;
};

// ==========================================:
const pgSettings = createSlice({
	name: '@@pgSettings',
	initialState,
	reducers: {
		// ---- Services (03 §4.1)
		getPgServicesRequest: (state) => {
			state.servicesLoading = true;
		},
		getPgServicesSuccess: (state, action: PayloadAction<TPgService[]>) => {
			state.services = action.payload;
			state.servicesLoading = false;
		},
		getPgServicesFailure: (state) => {
			state.servicesLoading = false;
		},
		createPgServiceRequest: (state, _action: PayloadAction<TPgCreateServicePayload>) => {
			startSave(state, 'service');
		},
		updatePgServiceRequest: (state, _action: PayloadAction<TPgUpdateServicePayload>) => {
			startSave(state, 'service');
		},

		// ---- service_type mappings (03 §4.1)
		getPgServiceTypeMappingsRequest: (state) => {
			state.serviceTypeMappingsLoading = true;
		},
		getPgServiceTypeMappingsSuccess: (state, action: PayloadAction<TPgServiceTypeMapping[]>) => {
			state.serviceTypeMappings = action.payload;
			state.serviceTypeMappingsLoading = false;
		},
		getPgServiceTypeMappingsFailure: (state) => {
			state.serviceTypeMappingsLoading = false;
		},
		upsertPgServiceTypeMappingRequest: (
			state,
			_action: PayloadAction<TPgUpsertServiceTypeMappingPayload>,
		) => {
			startSave(state, 'serviceTypeMapping');
		},
		deletePgServiceTypeMappingRequest: (state, _action: PayloadAction<string>) => {
			startSave(state, 'serviceTypeMapping');
		},

		// ---- Debt transfer rules (03 §4.2)
		getPgPayabilityRulesRequest: (state) => {
			state.payabilityRulesLoading = true;
		},
		getPgPayabilityRulesSuccess: (state, action: PayloadAction<TPgPayabilityRule[]>) => {
			state.payabilityRules = action.payload;
			state.payabilityRulesLoading = false;
		},
		getPgPayabilityRulesFailure: (state) => {
			state.payabilityRulesLoading = false;
		},
		createPgPayabilityRuleRequest: (
			state,
			_action: PayloadAction<TPgCreatePayabilityRulePayload>,
		) => {
			startSave(state, 'payabilityRule');
		},
		updatePgPayabilityRuleRequest: (
			state,
			_action: PayloadAction<TPgUpdatePayabilityRulePayload>,
		) => {
			startSave(state, 'payabilityRule');
		},
		deletePgPayabilityRuleRequest: (state, _action: PayloadAction<number>) => {
			startSave(state, 'payabilityRule');
		},

		// ---- Legal entities (03 §4.4)
		getPgLegalEntitiesRequest: (state) => {
			state.legalEntitiesLoading = true;
		},
		getPgLegalEntitiesSuccess: (state, action: PayloadAction<TPgLegalEntity[]>) => {
			state.legalEntities = action.payload;
			state.legalEntitiesLoading = false;
		},
		getPgLegalEntitiesFailure: (state) => {
			state.legalEntitiesLoading = false;
		},
		createPgLegalEntityRequest: (state, _action: PayloadAction<TPgCreateLegalEntityPayload>) => {
			startSave(state, 'legalEntity');
		},
		updatePgLegalEntityRequest: (state, _action: PayloadAction<TPgUpdateLegalEntityPayload>) => {
			startSave(state, 'legalEntity');
		},

		// ---- Airwallex accounts (03 §4.4)
		getPgAirwallexAccountsRequest: (state) => {
			state.airwallexAccountsLoading = true;
		},
		getPgAirwallexAccountsSuccess: (state, action: PayloadAction<TPgAirwallexAccount[]>) => {
			state.airwallexAccounts = action.payload;
			state.airwallexAccountsLoading = false;
		},
		getPgAirwallexAccountsFailure: (state) => {
			state.airwallexAccountsLoading = false;
		},
		updatePgAirwallexAccountRequest: (
			state,
			_action: PayloadAction<TPgUpdateAirwallexAccountPayload>,
		) => {
			startSave(state, 'airwallexAccount');
		},

		// ---- Fees (03 §4.5); payload: legal entity id, `null` — the default one
		getPgFeeMatrixRequest: (state, _action: PayloadAction<number | null>) => {
			state.feeMatrixLoading = true;
		},
		getPgFeeMatrixSuccess: (state, action: PayloadAction<TPgFeeMatrix>) => {
			state.feeMatrix = action.payload;
			state.feeMatrixLoading = false;
		},
		getPgFeeMatrixFailure: (state) => {
			state.feeMatrixLoading = false;
		},
		updatePgFeesRequest: (state, _action: PayloadAction<TPgUpdateFeesPayload>) => {
			startSave(state, 'fees');
		},

		// ---- DD FX settings (03 §4.5)
		getPgFxSettingsRequest: (state, _action: PayloadAction<number | null>) => {
			state.fxSettingsLoading = true;
		},
		getPgFxSettingsSuccess: (state, action: PayloadAction<TPgFxSettings>) => {
			state.fxSettings = action.payload;
			state.fxSettingsLoading = false;
		},
		getPgFxSettingsFailure: (state) => {
			state.fxSettingsLoading = false;
		},
		updatePgFxSettingsRequest: (state, _action: PayloadAction<TPgUpdateFxSettingsPayload>) => {
			startSave(state, 'fxSettings');
		},

		// ---- Schedule (03 §4.6)
		getPgScheduleRequest: (state) => {
			state.scheduleLoading = true;
		},
		getPgScheduleSuccess: (state, action: PayloadAction<TPgScheduleSettings>) => {
			state.schedule = action.payload;
			state.scheduleLoading = false;
		},
		getPgScheduleFailure: (state) => {
			state.scheduleLoading = false;
		},
		updatePgScheduleRequest: (state, _action: PayloadAction<TPgUpdateSchedulePayload>) => {
			startSave(state, 'schedule');
		},

		// ---- Customer access, EAS updates pause and customer e-mails
		getPgClientAccessRequest: (state) => {
			state.clientAccessLoading = true;
		},
		getPgClientAccessSuccess: (state, action: PayloadAction<TPgClientAccessSettings>) => {
			state.clientAccess = action.payload;
			state.clientAccessLoading = false;
		},
		getPgClientAccessFailure: (state) => {
			state.clientAccessLoading = false;
		},
		updatePgClientAccessRequest: (state, _action: PayloadAction<TPgUpdateClientAccessPayload>) => {
			startSave(state, 'clientAccess');
		},

		// ---- Runs and monthly cycles (03 §4.6)
		getPgRecurringRunsRequest: (state, action: PayloadAction<TPgListRequest>) => {
			state.recurringRunsLoading = true;
			state.recurringRunsLastParams = action.payload;
		},
		getPgRecurringRunsSuccess: (state, action: PayloadAction<TPgListResponse<TPgRecurringRun>>) => {
			state.recurringRuns = action.payload;
			state.recurringRunsLoading = false;
		},
		getPgRecurringRunsFailure: (state) => {
			state.recurringRunsLoading = false;
		},
		/** Re-reads the runs with the last parameters (after a manual run was started). */
		refreshPgRecurringRunsRequest: (state) => {
			state.recurringRunsLoading = true;
		},
		getPgRecurringCyclesRequest: (state, _action: PayloadAction<TPgListRequest>) => {
			state.recurringCyclesLoading = true;
		},
		getPgRecurringCyclesSuccess: (
			state,
			action: PayloadAction<TPgListResponse<TPgRecurringCycle>>,
		) => {
			state.recurringCycles = action.payload;
			state.recurringCyclesLoading = false;
		},
		getPgRecurringCyclesFailure: (state) => {
			state.recurringCyclesLoading = false;
		},

		// ---- Save flags
		pgSaveSuccess: (state, action: PayloadAction<TPgSaveKey>) => {
			state.saveLoading[action.payload] = false;
			state.saveSuccess[action.payload] = true;
		},
		pgSaveFailure: (state, action: PayloadAction<TPgSaveKey>) => {
			state.saveLoading[action.payload] = false;
		},
		resetPgSaveSuccess: (state, action: PayloadAction<TPgSaveKey>) => {
			state.saveSuccess[action.payload] = false;
		},

		pgSettingsInitState: () => initialState,
	},
});

export default pgSettings.reducer;
export const {
	getPgServicesRequest,
	getPgServicesSuccess,
	getPgServicesFailure,
	createPgServiceRequest,
	updatePgServiceRequest,
	getPgServiceTypeMappingsRequest,
	getPgServiceTypeMappingsSuccess,
	getPgServiceTypeMappingsFailure,
	upsertPgServiceTypeMappingRequest,
	deletePgServiceTypeMappingRequest,
	getPgPayabilityRulesRequest,
	getPgPayabilityRulesSuccess,
	getPgPayabilityRulesFailure,
	createPgPayabilityRuleRequest,
	updatePgPayabilityRuleRequest,
	deletePgPayabilityRuleRequest,
	getPgLegalEntitiesRequest,
	getPgLegalEntitiesSuccess,
	getPgLegalEntitiesFailure,
	createPgLegalEntityRequest,
	updatePgLegalEntityRequest,
	getPgAirwallexAccountsRequest,
	getPgAirwallexAccountsSuccess,
	getPgAirwallexAccountsFailure,
	updatePgAirwallexAccountRequest,
	getPgFeeMatrixRequest,
	getPgFeeMatrixSuccess,
	getPgFeeMatrixFailure,
	updatePgFeesRequest,
	getPgFxSettingsRequest,
	getPgFxSettingsSuccess,
	getPgFxSettingsFailure,
	updatePgFxSettingsRequest,
	getPgScheduleRequest,
	getPgScheduleSuccess,
	getPgScheduleFailure,
	updatePgScheduleRequest,
	getPgClientAccessRequest,
	getPgClientAccessSuccess,
	getPgClientAccessFailure,
	updatePgClientAccessRequest,
	getPgRecurringRunsRequest,
	getPgRecurringRunsSuccess,
	getPgRecurringRunsFailure,
	refreshPgRecurringRunsRequest,
	getPgRecurringCyclesRequest,
	getPgRecurringCyclesSuccess,
	getPgRecurringCyclesFailure,
	pgSaveSuccess,
	pgSaveFailure,
	resetPgSaveSuccess,
	pgSettingsInitState,
} = pgSettings.actions;
